/**
 * The customer's member-pass payload — the string a barista's scanner reads.
 *
 * WHY THIS EXISTS (2026-08-02): this code was previously generated ONLY inside
 * the Apple and Google wallet-pass builders on the server. A customer's only
 * route to a scannable QR was: select a shop, be on the right platform, tap
 * "Add to Wallet", then leave BitBrew and open the Wallet app. Anyone who had
 * not done all four — every customer who signed up in the last five minutes,
 * for instance — had NOTHING to show, while the shop owner's counter UI said
 * "SCAN MEMBER PASS". The pass is now rendered in-app too, and both carriers
 * must emit the identical string.
 *
 * VALIDATION IS DELIBERATELY STRICT because failure here is SILENT. The scanner
 * ignores any payload it cannot parse — on purpose, so pointing a camera at a
 * foreign QR does not throw — which means a malformed code does not error, it
 * just does nothing while the customer and the barista stare at each other.
 * Better to render no QR at all than one that cannot work.
 */

// Copied verbatim from the server: api/_lib/lite.ts:146 and api/stamp.ts:333.
// If the server relaxes or tightens this, change it here in the same commit.
const ADDRESS_RE = /^0x[0-9a-fA-F]+$/;
const SHOP_ID_RE = /^[0-9a-f-]{36}$/;

/**
 * @param customerAddress the customer's zkLogin Sui address
 * @param shopId the shop's SUPABASE UUID — not the shop owner's address. Both
 *        are called "the shop" in different code paths, and passing the wrong
 *        one produces a code the server rejects, so it is checked.
 * @returns the payload, or null when it could not be built correctly
 */
export function buildMemberPassCode(
  customerAddress: string | undefined | null,
  shopId: string | undefined | null,
): string | null {
  if (!customerAddress || !shopId) return null;
  if (!ADDRESS_RE.test(customerAddress)) return null;
  if (!SHOP_ID_RE.test(shopId)) return null;
  return `bitbrew:v1:${customerAddress}:${shopId}`;
}
